import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';

import Box from '@mui/material/Box';

import {
  ResolverReturnTypeEnum,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { ensureDefined } from '@e-socna/utils';
import type { ResolverEnum } from '@e-socna/data-layer';

import { ResolverSelectionDialog } from '../../components/ResolverSelectionDialog';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { unwrapExpression } from '../../utils/handlebars-expression.util';
import type { OnSelectHandler } from '../../components/ResolverList/types/on-select-handler.type';

import { SelectField } from './SelectField';
import { TextField } from './TextField';
import { FieldAdornment } from './FieldAdornment';
import { DEFAULT_MODE } from './constants/default-mode.constant';
import type { ResolvableFieldProps } from './types/props.type';

export const ResolvableField = (props: Readonly<ResolvableFieldProps>) => {
  const { defaultStaticValue, name, resolvableFieldSettings, type, value } =
    props;

  const currentMode = value?.mode ?? DEFAULT_MODE;

  const [isResolverSelectionDialogOpen, setIsResolverSelectionDialogOpen] =
    useState(false);
  const handleCloseResolverSelectionDialog = useCallback(() => {
    setIsResolverSelectionDialogOpen(false);
  }, []);
  const handleOpenResolverSelectionDialog = useCallback(() => {
    setIsResolverSelectionDialogOpen(true);
  }, []);

  const { resolvers, resolverReturnTypesCodeMap } = useReferencesContext();
  const resolverReturnType = resolverReturnTypesCodeMap[type];

  const { setFieldValue, setFieldTouched } = useFormikContext();

  // setFieldTouched must not revalidate: it would run on the values from before
  // the pending setFieldValue and could overwrite its fresh validation result.

  const handleSelectResolver = useCallback<OnSelectHandler>(
    (selectedType, value) => {
      switch (selectedType) {
        case 'command': {
          const { resolver } = ensureDefined({
            resolver: resolvers.find(
              (resolver) =>
                resolver.command === (unwrapExpression(value) as ResolverEnum),
            ),
          });

          void setFieldValue(name, {
            __typename: resolverReturnType.typeName,
            mode: ResolvableModeEnum.Dynamic,
            resolverId: resolver._id,
            value: undefined,
          });

          void setFieldTouched(name, true, false);

          handleCloseResolverSelectionDialog();

          return;
        }
        case 'directGeneration': {
          const { convertedValue } = ensureDefined({
            convertedValue: [
              ResolverReturnTypeEnum.CurrencyId,
              ResolverReturnTypeEnum.UnitId,
              ResolverReturnTypeEnum.VatId,
            ].includes(type)
              ? value
              : [ResolverReturnTypeEnum.Float].includes(type)
                ? parseFloat(value)
                : [ResolverReturnTypeEnum.Boolean].includes(type)
                  ? value === 'true'
                  : undefined,
          });

          void setFieldValue(name, {
            __typename: resolverReturnType.typeName,
            mode: ResolvableModeEnum.Static,
            resolverId: undefined,
            value: convertedValue,
          });

          void setFieldTouched(name, true, false);

          handleCloseResolverSelectionDialog();

          return;
        }
        default:
          throw new Error(`Unsupported type of resolver selection`);
      }
    },
    [
      handleCloseResolverSelectionDialog,
      name,
      resolvers,
      resolverReturnType.typeName,
      setFieldTouched,
      setFieldValue,
      type,
    ],
  );

  const handleSwitchToDefaultStatic = useCallback(() => {
    void setFieldValue(name, {
      __typename: resolverReturnType.typeName,
      mode: ResolvableModeEnum.Static,
      resolverId: undefined,
      value: defaultStaticValue,
    });

    void setFieldTouched(name, true, false);
  }, [
    name,
    defaultStaticValue,
    resolverReturnType.typeName,
    setFieldTouched,
    setFieldValue,
  ]);

  const { fieldComponent } = ensureDefined({
    fieldComponent:
      currentMode === ResolvableModeEnum.Dynamic ? (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          onOpenResolverSelectionDialog={handleOpenResolverSelectionDialog}
        />
      ) : [
          ResolverReturnTypeEnum.Boolean,
          ResolverReturnTypeEnum.CurrencyId,
          ResolverReturnTypeEnum.UnitId,
          ResolverReturnTypeEnum.VatId,
        ].includes(type) ? (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <SelectField {...props} />
      ) : [ResolverReturnTypeEnum.Float].includes(type) ? (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          onOpenResolverSelectionDialog={handleOpenResolverSelectionDialog}
        />
      ) : undefined,
  });

  return (
    <>
      <Box sx={{ alignItems: 'start', display: 'flex' }}>
        {fieldComponent}
        <FieldAdornment
          name={name}
          onOpen={handleOpenResolverSelectionDialog}
          onSwitchToDefaultStatic={handleSwitchToDefaultStatic}
          value={value}
        />
      </Box>
      <ResolverSelectionDialog
        isOpen={isResolverSelectionDialogOpen}
        onClose={handleCloseResolverSelectionDialog}
        onSelect={handleSelectResolver}
        resolversSettings={{
          ...resolvableFieldSettings,
          allowedReturnTypeIds: [resolverReturnType._id],
          mustBeResolvableOnBack: true,
          mustBeResolvableOnFront: true,
        }}
      />
    </>
  );
};
